// Opaque checkpoint-token rules, shared by mobile/web/worker (ADR-005).
// The physical QR/NFC artifact carries ONLY a random opaque token of the form
// `chk_<high-entropy alphanumeric>` — never a URL, a checkpoint/route/client name,
// or any human-readable value. Tokens are GENERATED server-side (CSPRNG, account
// scoped); this module only validates and normalizes scanned input.

/**
 * Canonical checkpoint-token format: `chk_` prefix + 16–128 base62 chars.
 * Example: chk_8Jf29xQp7bR4sT6v
 * This regex is the contract — the server-side generator must produce tokens
 * that satisfy it, and every scan surface validates against it.
 */
const CHECKPOINT_TOKEN_PATTERN = /^chk_[A-Za-z0-9]{16,128}$/;

/** Thrown when a value cannot be used as a checkpoint token. */
export class CheckpointTokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CheckpointTokenError";
  }
}

/** True iff the trimmed value matches the canonical opaque token format. Never throws. */
export function isValidCheckpointToken(value: string): boolean {
  return typeof value === "string" && CHECKPOINT_TOKEN_PATTERN.test(value.trim());
}

/**
 * Trim a scanned value and return the canonical token. Throws CheckpointTokenError
 * on empty, whitespace-only, or non-token input (URLs, readable names, wrong
 * prefix, bad charset/length).
 */
export function normalizeCheckpointToken(value: string): string {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (trimmed.length === 0) {
    throw new CheckpointTokenError("Checkpoint token is empty.");
  }
  if (!CHECKPOINT_TOKEN_PATTERN.test(trimmed)) {
    throw new CheckpointTokenError(
      'Not a valid checkpoint token. Expected an opaque "chk_" token, not a URL or readable name.',
    );
  }
  return trimmed;
}

/**
 * Call-site guard: returns the normalized valid token or throws. Delegates to
 * normalizeCheckpointToken so validation never diverges between the two.
 */
export function assertCheckpointToken(value: string): string {
  return normalizeCheckpointToken(value);
}
